import { useEffect, useRef, useState } from 'react'
import { arrivalsSince, NOTHING_WATCHED, type Placed, type Watched } from '../lib/arrivals'

/** Long enough to find with your eye, short enough not to become decoration. */
export const RING_MS = 1400

/**
 * The ids of cards that changed column recently, so the board can ring them.
 *
 * The decision lives in `lib/arrivals`; this is the part that needs React.
 */
export function useJustArrived(items: readonly Placed[]): ReadonlySet<string> {
  const watched = useRef<Watched>(NOTHING_WATCHED)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const [ringing, setRinging] = useState<ReadonlySet<string>>(() => new Set())

  useEffect(() => {
    // Runs on every render, because a filtered list is a new array every
    // render. That is fine and deliberate: with nothing moved it does no work
    // and sets no state, so there is no loop to fall into.
    const { moved, after } = arrivalsSince(watched.current, items)
    watched.current = after
    if (moved.length === 0) return

    setRinging((prev) => new Set([...prev, ...moved]))

    // Deliberately not cleaned up when the effect re-runs. The effect re-runs
    // constantly, and a cleanup here would cancel the timer that takes the
    // ring off — leaving every card that ever moved lit up for good.
    timers.current.push(
      setTimeout(() => {
        setRinging((prev) => {
          const next = new Set(prev)
          for (const id of moved) next.delete(id)
          return next
        })
      }, RING_MS)
    )
  }, [items])

  useEffect(
    () => () => {
      for (const timer of timers.current) clearTimeout(timer)
    },
    []
  )

  return ringing
}
